import type { ReactNode } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'

import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import classnames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import PulsingDot, { DotSize } from '~/components/atoms/PulsingDot/PulsingDot'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { useInPageNavContext } from '~/contexts/InPageNavContext'
import { useSearchAnalyticsContext } from '~/contexts/SearchAnalyticsContext'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import type { FetchedSettings } from '~/services/sanity/siteSettings'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { getRouterPath } from '~/utils/getRouterPath'
import { PushInteractionLocation } from '~/utils/posthogEvents'
import { useScrollPosition } from '~/utils/useScrollPosition'

import styles from './ArtworksList.module.css'

const Artworks = dynamic(() => import('./Artworks'), { ssr: true })
const SearchLozenge = dynamic(() => import('../SearchLozenge/SearchLozenge'))
const Filters = dynamic(() => import('../Filters/Filters'))
const TradingToggle = dynamic(() => import('../TradingToggle/TradingToggle'), {
  ssr: false,
})

export type filtersType = {
  genres: string[]
  formats: string[]
  artists: string[]
  prices: number[]
  tradingFloor?: 'owned' | 'buy' | 'sell'
}

export type formatsType = {
  _id: string
  slug: string
  name: string
  count?: number
}[]

export interface ArtworksListProps {
  pageSize: number
  testID?: string
  queryId?: string
  fallbackData?: any
  type?: 'artist' | 'collection' | 'genre'
  genres?: FetchedSettings['genres']
  formats?: formatsType
  sectionId?: string
  slugParams?: boolean
  startPage?: number
  searchPlaceholder?: string
  fallBackPaginationHandler?: () => any
  searchInsightsEvents?: {
    artwork: TSearchAnalystEventProps
    artist?: TSearchAnalystEventProps
    filter?: TSearchAnalystEventProps
    catalogue?: TSearchAnalystEventProps
  }
  artistName?: string
  hideFilters?: string[]
  hideTradingFloor?: boolean
  controlsTopZero?: boolean
  totalArtworks?: number
  overrideTitle?: string
  summary?: string
  noArtistLink?: boolean
  stickyPush?: boolean
  initialFilters?: Partial<filtersType>
}

export default function ArtworksList({
  pageSize,
  queryId,
  fallbackData,
  type,
  sectionId,
  slugParams,
  startPage,
  searchPlaceholder,
  testID,
  fallBackPaginationHandler,
  searchInsightsEvents,
  artistName,
  hideFilters,
  hideTradingFloor = false,
  controlsTopZero = false,
  totalArtworks,
  overrideTitle,
  summary,
  noArtistLink,
  stickyPush = false,
  initialFilters,
}: ArtworksListProps) {
  const t = useTranslations('common')
  const tNavigation = useTranslations('navigation')
  const router = useRouter()

  const { mainHeaderVisible } = useInPageNavContext()
  const [formats, setFormats] = useState<any>()
  const [genres, setGenres] = useState<any>()
  const [artists, setArtists] = useState<any>()
  const [prices, setPricesCondition] = useState<any>()
  const [total, setTotal] = useState<number | undefined>()
  const [totalTitle] = useState<string>(tNavigation('artworks'))
  const [searchTerm, setSearchTerm] = useState('')
  const [pageNumber, setPageNumber] = useState<number>(
    startPage ? startPage : 1
  )

  // Track whether initial filters have been applied
  const hasAppliedInitialFilters = useRef(false)

  const { setCurrentSearchTerm, submitSearchEvent, resetSearch } =
    useSearchAnalyticsContext()

  const [isNotSticky, setIsNotSticky] = useState(true)
  const [initialFilterPosition, setInitialFilterPosition] = useState<
    number | null
  >(null)

  const elementRef: any = useRef(null)
  const artworksRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)

  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)

  const { state } = useContext(GlobalStoreContext)

  const isFilterable = type !== 'collection'
  const viewAll =
    (type === 'artist' || type === 'collection') &&
    state.artworks.currentArtworks.total > 12
      ? true
      : false

  const setFormatCondition = (arr: string[]) => {
    setFormats(arr)
  }
  const setGenreCondition = (arr: string[]) => {
    if (type !== 'genre') setGenres(arr)
  }

  const setArtistCondition = (arr: string[]) => {
    if (type !== 'artist') setArtists(arr)
  }

  const updateFacets = (facets: any, total: number) => {
    setFormatCondition(facets?.format || [])
    setGenreCondition(facets?.genre || [])
    setArtistCondition(facets?.artist || [])
    setPricesCondition(facets?.price || [])
    setTotal(total)
  }

  useEffect(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop

    const position = elementRef?.current?.getBoundingClientRect().top
    const height = elementRef?.current?.getBoundingClientRect().height

    setInitialFilterPosition(scrollTop + position + height - 120)
  }, [])

  // Apply initial filters to URL if needed
  useEffect(() => {
    if (initialFilters?.tradingFloor && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      if (!urlParams.has('trading-floor')) {
        urlParams.set('trading-floor', initialFilters.tradingFloor)
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}?${urlParams.toString()}`
        )
      }
    }
  }, [])

  let filters = {
    artists: Array(0),
    genres: Array(0),
    formats: Array(0),
    prices: Array(0),
    tradingFloor: false as any,
  }
  const hasFilters = artists || formats || genres ? true : false

  if (typeof window === 'object') {
    const urlParams = new URLSearchParams(window?.location.search)

    // Apply initial filters to URL if needed (only for AB test variant)
    // Only apply once per component lifecycle
    if (
      initialFilters?.tradingFloor &&
      !urlParams.has('trading-floor') &&
      !hasAppliedInitialFilters.current
    ) {
      hasAppliedInitialFilters.current = true
      urlParams.set('trading-floor', initialFilters.tradingFloor)
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}?${urlParams.toString()}`
      )
    }

    // Now read all filters from URL (including any we just set)
    const genreFilters = urlParams?.get('genres')
      ? JSON.parse(String(urlParams?.get('genres')))
      : []
    const artistFilters = urlParams?.get('artists')
      ? JSON.parse(String(urlParams?.get('artists')))
      : []
    const formatFilters = urlParams?.get('formats')
      ? JSON.parse(String(urlParams?.get('formats')))
      : []
    const priceFilters = urlParams?.get('prices')
      ? JSON.parse(String(urlParams?.get('prices')))
      : []

    // Read from URL only - no fallback to initialFilters
    const tradingFloor = urlParams?.get('trading-floor') || false

    filters = {
      artists: artistFilters,
      genres: genreFilters,
      formats: formatFilters,
      prices: priceFilters,
      tradingFloor: tradingFloor,
    }
  }

  useEffect(() => {
    // Are we serving results through a true url /slug/page/2
    if (slugParams) {
      // Do we have a number for the last part of the slug? If so we are going to paginate
      if (router?.query.slug) {
        if (router?.query.page) setPageNumber(Number(router?.query.page))
        else setPageNumber(1)
      }
    } else {
      // Or is it through a query param ?p=2
      // FILL THIS IN
      const q = router.asPath.split('?').reverse()[0]

      if (q.indexOf('p=') !== -1 && router?.query.p) {
        setPageNumber(Number(router?.query.p))
      }
      if (q.indexOf('p=') === -1) {
        setPageNumber(Number(1))
      }
    }
  }, [router?.query.slug, router?.query.page, router?.query.p])

  useScrollPosition(
    ({ currPos }) => {
      setIsNotSticky(currPos.y > Number(`-${initialFilterPosition}`))
    },
    [initialFilterPosition]
  )

  const currentPathname = getRouterPath(router)

  const isTradingFloorPage =
    currentPathname.endsWith('/trading-floor') ||
    currentPathname === '/trading-floor' ||
    currentPathname.endsWith('/salle-des-marches') ||
    currentPathname === '/salle-des-marches'

  let title: string | ReactNode = t('artForSale')
  if (artistName)
    title = `${artistName && artistName} ${t('artForSale').toLowerCase()}`

  //if (type == 'artist') title = `${totalArtworks} ${title}`
  // if (artistName === 'Banksy' && type == 'artist')
  //   title = `${totalArtworks} ${title}`

  if (isTradingFloorPage) {
    title = t.rich('liveTrading', {
      em: chunks => <em>{chunks}</em>,
    })
  }

  if (overrideTitle) {
    title = overrideTitle
  }

  return (
    <>
      <Container>
        <div className={styles.titleContainer}>
          <div className={classnames(styles.title)}>
            <Text
              Tag="h2"
              size={
                isTradingFloorPage && isMobile
                  ? TextSizes.primaryTitleSml
                  : TextSizes.primaryTitleMed
              }
              id={sectionId}
            >
              {typeof title === 'string' ? (
                <span dangerouslySetInnerHTML={{ __html: title }} />
              ) : (
                <>{title}</>
              )}
            </Text>
            {isTradingFloorPage && <PulsingDot size={DotSize.large} />}
          </div>
          <TradingToggle />
        </div>
        {summary && (
          <Text className={styles.summary} Tag="p" size={TextSizes.bodyMed}>
            {summary}
          </Text>
        )}
        <div className={styles.keyLine}></div>
      </Container>
      <div>
        {isFilterable && (
          <div
            ref={elementRef}
            className={classnames(
              styles.controlsWrap,
              stickyPush && styles.controlsWrapPush,
              !isNotSticky && styles.controlsWrapInView,
              controlsTopZero && styles.controlsWrapZeroTop,
              state.headerTabOpen && styles.controlsWrapLowerZ,
              stickyPush && mainHeaderVisible && styles.stickyMainHeaderVisible
            )}
          >
            <Container>
              <div
                className={classnames(
                  styles.controls,
                  isNotSticky && styles.controlsReverse,
                  !isNotSticky && styles.controlsSticky
                )}
              >
                <SearchLozenge
                  artistName={artistName}
                  isLoading={isLoading}
                  handleResults={st => {
                    setSearchTerm(st)
                    setCurrentSearchTerm(
                      st,
                      PushInteractionLocation.InnerPageSearch
                    )
                    if (st === '') {
                      resetSearch()
                    }
                  }}
                  className={styles.search}
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  filterPosition={initialFilterPosition}
                />

                {hasFilters && (
                  <Filters
                    filters={filters}
                    genres={genres}
                    artists={artists}
                    formats={formats}
                    prices={prices}
                    viewAll={viewAll}
                    searchTerm={searchTerm}
                    testID={testID && `artworkslist-${testID}`}
                    clearSearchTerm={() => {
                      setSearchTerm('')
                      resetSearch()
                      delete router.query.aw
                    }}
                    filterQueries={
                      queryId ? [{ term: { artistId: queryId } }] : undefined
                    }
                    totals={{ total: total || 0, totalTitle }}
                    searchInsightsEvents={searchInsightsEvents}
                    hideFilters={hideFilters}
                    hideTradingFloor={!isTradingFloorPage}
                    pushRight={isNotSticky}
                    initialFilters={initialFilters}
                  />
                )}
              </div>
            </Container>
          </div>
        )}

        <Container>
          <div ref={artworksRef}>
            <Artworks
              pageSize={pageSize}
              queryId={queryId}
              fallbackData={fallbackData}
              fallBackPaginationHandler={fallBackPaginationHandler}
              type={type}
              filters={filters}
              pagenumber={pageNumber}
              slugParams={slugParams}
              setFacets={(facets: any, total: number) =>
                updateFacets(facets, total)
              }
              searchTerm={searchTerm}
              setIsloading={setIsLoading}
              searchInsightsEvents={searchInsightsEvents}
              noArtistLink={noArtistLink}
            />
          </div>
        </Container>
      </div>
    </>
  )
}
